import React, { useState, useEffect } from 'react';
import PortalModal from '../../Common/Modal/PortalModal';
import { XMarkIcon } from '@heroicons/react/24/outline';

const MergeConfigurationsModal = React.forwardRef(({ capsules, selectedCapsules, handleMergeConfigurations, hideModal }, ref) => {
    const [resetSensibilities, setResetSensibilities] = useState(false);
    const [blendCounts, setBlendCounts] = useState({ physical: 0, effluence: 0 });
    const [capsuleDetails, setCapsuleDetails] = useState([]);
    
    
    useEffect(() => {
        // Calculate blend counts from selected capsules' initial configurations
        const calculateBlendCounts = () => {
            let physicalCount = 0;
            let effluenceCount = 0;
            const details = [];

            selectedCapsules.forEach(capsuleId => {
                const capsule = capsules.find(c => c.id === capsuleId);
                if (capsule && capsule.configurations && capsule.configurations.length > 0) {
                    // Use initial configuration (first one)
                    const initialConfig = capsule.configurations[0];
                    
                    const capsulePhysical = initialConfig.configuration.totalPhysique + initialConfig.configuration.totalOther;
                    const capsuleEffluence = initialConfig.configuration.totalEffluence;
                    
                    physicalCount += capsulePhysical;
                    effluenceCount += capsuleEffluence;
                    
                    details.push({
                        id: capsule.id,
                        name: capsule.name,
                        physical: capsulePhysical,
                        effluence: capsuleEffluence
                    });
                }
            });

            setBlendCounts({
                physical: physicalCount,
                effluence: effluenceCount
            });
            
            setCapsuleDetails(details);
        };

        calculateBlendCounts();
    }, [selectedCapsules, capsules]);

    return (
        <PortalModal
        id="merge-configurations-modal"
        layout={false}
        ref={ref}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl mx-auto overflow-hidden">
                <div className="flex justify-between items-center bg-primary p-4 text-white">
                    <h1 className="text-xl font-bold">Fusion des capsules</h1>
                    <button 
                        onClick={hideModal}
                        className="text-white hover:text-base-300 hover:bg-base-100/20 rounded-full  transition-all duration-200 focus:outline-none"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>
                
                <div className="p-6">
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">Capsules sélectionnées</h2>
                        <div className="space-y-3 max-h-56 overflow-y-auto pr-2">
                            {capsuleDetails.map(capsule => (
                                <div key={capsule.id} className="bg-gray-50 p-3 rounded-lg shadow-sm flex justify-between">
                                    <span className="font-medium text-gray-800 truncate"><span className='text-sm text-gray-500'>CAPSULE:</span> {capsule.name}</span>
                                    <div className="flex gap-3">
                                        <span className="text-sm px-2 py-1 bg-primary text-primary-content rounded-full truncate">
                                            {capsule.physical} physiques
                                        </span>
                                        <span className="text-sm px-2 py-1 bg-primary text-primary-content rounded-full truncate">
                                            {capsule.effluence} effluences
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <h2 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">Résumé de la fusion</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="border border-primary p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-base-content">{blendCounts.physical}</div>
                                <div className="text-sm text-gray-600">Physiques</div>
                            </div>
                            <div className="border border-primary p-4 rounded-lg text-center">
                                <div className="text-2xl font-bold text-base-content">{blendCounts.effluence}</div>
                                <div className="text-sm text-gray-600">Effluences</div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mb-6">
                        <input
                            id="resetSensibilities"
                            type="checkbox"
                            checked={resetSensibilities}
                            onChange={() => setResetSensibilities(!resetSensibilities)}
                            className="checkbox checkbox-primary w-5 h-5"
                        />
                        <label htmlFor="resetSensibilities" className="text-gray-700 cursor-pointer">
                            Réinitialiser les sensibilités
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 pt-2 border-t">
                        <button 
                            className="btn btn-ghost"
                            onClick={hideModal}
                        >
                            Annuler
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={handleMergeConfigurations(resetSensibilities)}
                            disabled={selectedCapsules.length < 2}
                        >
                            Fusionner
                        </button>
                    </div>
                </div>
            </div>
        </PortalModal>
    );
});

export default MergeConfigurationsModal;
